<template>
	<footer v-if="contents" class="bg-black text-white">
		<!-- Row 1 -->
		<div
			v-if="contents.content.cta.length > 0"
			class="container mx-auto border border-x-0 border-t-0 border-solid border-white/20 py-md lg:py-3xl"
		>
			<div class="flex flex-col justify-center xl:mx-2xl xl:px-2xl">
				<!-- Call to action email field-->
				<div
					v-for="cta in contents.content.cta"
					:key="cta._uid"
					class="text-center"
				>
					<v-storyblok-component :blok="cta" />
				</div>
			</div>
		</div>

		<!-- Row 2 -->
		<div
			class="container mx-auto flex flex-row justify-between border border-x-0 border-t-0 border-solid border-white/20 px-sm py-md lg:py-2xl"
		>
			<!-- Logo(s) column -->
			<div class="flex-y order-2 w-48 items-center gap-6 md:order-0 xl:w-64">
				<nuxt-link to="/" aria-label="Masku logo">
					<v-icon
						:icon="footerLogo"
						class="mt-6 text-[5rem] md:text-12xl lg:text-[7rem] xl:text-[8rem]"
					></v-icon>
				</nuxt-link>

				<v-icon
					v-if="isExtraIconVisible"
					:icon="extraIcon"
					class="block stroke-white text-[5rem] text-white md:text-12xl lg:hidden lg:text-[8rem]"
				/>

				<!-- Small viewport icons -->
				<template v-for="logo in additionalLogos" :key="logo._uid">
					<component
						:is="logo.link ? NuxtLink : 'span'"
						:to="logo.link || undefined"
					>
						<nuxt-picture
							v-if="logo.icon?.filename"
							:src="logo.icon.filename"
							:alt="logo.icon.alt"
							provider="storyblok"
							loading="lazy"
							height="96"
							width="96"
							class="block size-20 md:size-24 lg:hidden"
						/>

						<v-icon
							v-else-if="logo.icon_svg"
							:icon="logo.icon_svg"
							class="block stroke-white text-[5rem] text-white md:text-12xl lg:hidden lg:text-[8rem]"
						/>
					</component>
				</template>
			</div>

			<!-- Menu items column -->
			<div
				class="order-1 grow md:grid md:grid-cols-2 lg:order-0 lg:grid-cols-4"
			>
				<div
					class="flex-y mb-md md:col-start-1 md:row-start-1 md:mx-lg lg:col-start-1 lg:row-start-1 lg:items-center"
				>
					<div class="flex-y">
						<p class="mb-sm text-lg font-bold">
							{{ contents.content.information_title }}
						</p>

						<nuxt-link
							v-for="link in contents.content.information"
							:key="link._uid"
							:to="formatSbUrl(link.link)"
							:aria-label="link.name"
							class="pb-xs text-sm tracking-xl hover:underline"
						>
							{{ link.name }}
						</nuxt-link>
					</div>
				</div>

				<div
					class="flex-y mb-md md:col-start-2 md:row-start-1 md:mx-lg lg:col-start-2 lg:row-start-1 lg:items-center"
				>
					<div class="flex-y items-start">
						<p class="mb-sm text-lg font-bold">
							{{ contents.content.buying_title }}
						</p>

						<nuxt-link
							v-for="link in contents.content.buying"
							:key="link._uid"
							:to="formatSbUrl(link.link)"
							:aria-label="link.name"
							class="pb-xs text-sm tracking-xl hover:underline"
						>
							{{ link.name }}
						</nuxt-link>
					</div>
				</div>

				<div
					class="flex-y mb-md md:col-start-1 md:row-start-2 md:mx-lg lg:col-start-3 lg:row-start-1 lg:items-center"
				>
					<div class="flex-y">
						<p class="mb-sm text-lg font-bold">
							{{ contents.content.support_title }}
						</p>

						<nuxt-link
							v-for="link in contents.content.support"
							:key="link._uid"
							:to="formatSbUrl(link.link)"
							:aria-label="link.name"
							class="pb-xs text-sm tracking-xl hover:underline"
						>
							{{ link.name }}
						</nuxt-link>
					</div>
				</div>

				<div
					class="flex-y mb-md md:col-start-2 md:row-start-2 md:mx-lg lg:col-start-4 lg:row-start-1 lg:items-center"
				>
					<div class="flex-y">
						<p class="mb-sm text-lg font-bold">
							{{ contents.content.services_title }}
						</p>

						<nuxt-link
							v-for="link in contents.content.services"
							:key="link._uid"
							:to="formatSbUrl(link.link)"
							:aria-label="link.name"
							class="pb-xs text-sm tracking-xl hover:underline"
						>
							{{ link.name }}
						</nuxt-link>
					</div>
				</div>
			</div>

			<!-- Extra icons column / large viewport icons -->
			<div
				class="lg:flex-y order-2 hidden w-48 items-center justify-center lg:gap-6 xl:w-64"
			>
				<v-icon
					v-if="isExtraIconVisible"
					:icon="extraIcon"
					class="stroke-white text-[5rem] text-white md:text-12xl lg:text-[8rem]"
				/>

				<template v-for="logo in additionalLogos" :key="logo._uid">
					<component
						:is="logo.link ? NuxtLink : 'span'"
						:to="logo.link || undefined"
					>
						<!-- The svg logo has font-size of 8rem = 128px -->
						<nuxt-picture
							v-if="logo.icon?.filename"
							:src="logo.icon.filename"
							:alt="logo.icon.alt"
							provider="storyblok"
							loading="lazy"
							height="128"
							width="128"
							class="size-32"
						/>

						<v-icon
							v-else-if="logo.icon_svg"
							:icon="logo.icon_svg"
							class="stroke-white text-[5rem] text-white md:text-12xl lg:text-[8rem]"
						/>
					</component>
				</template>
			</div>
		</div>

		<!-- Row 3 -->
		<div
			class="w-full px-sm py-md md:flex md:justify-center lg:grid lg:grid-cols-12"
		>
			<div class="flex flex-row lg:col-start-4 lg:col-end-12">
				<!-- Payment methods -->
				<div class="col-start-4 mr-md flex w-1/2 flex-row md:mx-lg">
					<div class="flex flex-col">
						<p class="mb-sm text-lg font-bold">
							{{ contents.content.payment_title }}
						</p>

						<div class="flex flex-row flex-wrap items-center gap-4">
							<nuxt-link
								v-for="link in contents.content.payment"
								:key="link._uid"
								:to="link.link || '#'"
								:aria-label="link.name"
								:title="tg('accessibility.read-more-payments')"
							>
								<nuxt-picture
									v-if="link.icon.filename"
									:src="link.icon.filename"
									loading="lazy"
									provider="storyblok"
									:alt="link.name"
									width="48"
									height="48"
									sizes="xs:120px sm:120px md:120px lg:120px xl:120px 2xl:120px"
									class="w-8 md:w-12"
								/>

								<!-- 48x48 sized icon (3rem * 3rem) -->
								<v-icon
									v-else-if="link?.icon_svg"
									:icon="link.icon_svg"
									class="text-8xl"
								/>
							</nuxt-link>
						</div>
					</div>
				</div>

				<!-- Social media icon links -->
				<div class="col-start-6 col-end-12 flex flex-col lg:mx-lg">
					<p class="mb-sm text-lg font-bold">
						{{ contents.content.social_title }}
					</p>

					<div class="flex flex-row items-center space-x-sm lg:space-x-lg">
						<nuxt-link
							v-for="link in contents.content.social"
							:key="link._uid"
							:to="link.link ? link.link : '#'"
							:aria-label="link.name"
							:title="tg('accessibility.open-social', {page: link.name})"
							target="_blank"
							class="aspect-square size-6 object-contain md:size-6 lg:size-5"
						>
							<nuxt-picture
								v-if="link.icon.filename && link.icon.filename.length > 0"
								:src="link.icon.filename"
								loading="lazy"
								provider="storyblok"
								:alt="link.name"
								width="48"
								height="48"
								:img-attrs="{
									class: 'h-full w-full object-contain',
								}"
								sizes="xs:120px sm:120px md:120px lg:120px xl:120px 2xl:120px"
								class="aspect-square size-6 object-contain md:size-6 lg:size-5"
							/>

							<v-icon
								v-else-if="link?.icon_svg"
								:icon="link.icon_svg"
								class="size-12"
							/>
						</nuxt-link>
					</div>
				</div>
			</div>
		</div>

		<!-- Row 4 -->
		<div
			class="w-full border border-x-0 border-b-0 border-solid border-white/20 px-sm py-md"
		>
			<div class="flex flex-col justify-between md:flex-row">
				<p class="md:order-0 lg:order-1">&copy; {{ t("masku") }}</p>

				<div class="flex flex-col md:order-1 md:flex-row lg:order-2">
					<a
						v-for="link in contents.content.links"
						:key="link._uid"
						:href="formatSbUrl(link.link)"
						:aria-label="link.name"
						class="py-xs text-sm tracking-xl text-white hover:underline md:px-sm md:py-0"
					>
						{{ link.name }}
					</a>
				</div>
			</div>
		</div>
	</footer>
</template>

<script setup lang="ts">
import type {Icon} from "@pkg/shared/types";

import {useStoryblokStory} from "@pkg/shared/composables/use-storyblok-story";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";
import {NuxtLink} from "#components";

const {t} = useI18n();

const {t: tg} = useI18n({useScope: "global"});

const {data: contents} = useStoryblokStory("global/footer");

/**
 * @deprecated This property is deprecated and will be removed in future versions.
 * Use the new implementation ('additionalLogos') for adding logos/images instead.
 */
const isExtraIconVisible = computed(
	() => contents.value?.content.show_additional_logo ?? false,
);
/**
 * @deprecated This property is deprecated and will be removed in future versions.
 * Use the new implementation ('additionalLogos') for adding logos/images instead.
 */
const extraIcon = computed(
	() => contents.value?.content.additional_logo ?? null,
);

const additionalLogos = computed(
	() => contents?.value?.content?.additional_logos ?? [],
);

const footerLogo = computed<Icon>(
	() => contents.value?.content?.footer_logo ?? "masku-2026",
);
</script>
